// Reviewed draft pictogram selections for every existing Easy Read sentence.
// Keys are journey source IDs, stage IDs, then the displayed sentence order.
// No generic fallback picture is inserted silently: missing mappings are flagged
// by the source-integrity tests and in the review UI.
export const EASY_READ_VISUAL_KEYS = Object.freeze({
  "generic-easy-read-outpatient-appointment": {
    "before-you-go": [
      "letter",
      "support",
      "medicine",
      "speech",
      "help"
    ],
    "arrive-and-check-in": [
      "id",
      "reception",
      "noise",
      "question"
    ],
    "waiting": [
      "health",
      "clock",
      "quiet",
      "next"
    ],
    "your-appointment": [
      "health",
      "doctor",
      "treatment",
      "question",
      "support"
    ],
    "after-your-appointment": [
      "calendar",
      "notes",
      "phone"
    ]
  },
  "generic-easy-read-scan": {
    "before-your-scan": [
      "scan",
      "letter",
      "clock",
      "question",
      "bag"
    ],
    "arrive": [
      "reception",
      "clock",
      "help"
    ],
    "get-ready": [
      "clothes",
      "metal",
      "arm",
      "medicine",
      "comfort",
      "question"
    ],
    "have-the-scan": [
      "still",
      "noise",
      "headphones",
      "stop",
      "question"
    ],
    "after-the-scan": [
      "home",
      "result",
      "calendar"
    ]
  },
  "generic-easy-read-blood-test": {
    "before-your-test": [
      "blood",
      "food",
      "letter",
      "needle",
      "comfort"
    ],
    "arrive": [
      "clock",
      "quiet",
      "clock",
      "support"
    ],
    "get-ready": [
      "chair",
      "arm",
      "speech"
    ],
    "take-the-blood": [
      "needle",
      "blood",
      "health",
      "pause"
    ],
    "after-the-test": [
      "home",
      "health",
      "result"
    ]
  },
  "generic-easy-read-emergency-department": {
    "arrive": [
      "ambulance",
      "id",
      "health",
      "speech",
      "help"
    ],
    "first-check": [
      "health",
      "pain",
      "allergy",
      "warning"
    ],
    "wait": [
      "noise",
      "clock",
      "quiet",
      "food",
      "medicine",
      "safety"
    ],
    "tests-and-treatment": [
      "doctor",
      "scan",
      "medicine",
      "speech"
    ],
    "what-happens-next": [
      "home",
      "warning",
      "phone"
    ]
  },
  "generic-easy-read-hospital-stay": {
    "arrive-on-the-ward": [
      "nurse",
      "speech",
      "help",
      "passport",
      "notes"
    ],
    "daily-care": [
      "health",
      "medicine",
      "food",
      "pain",
      "help"
    ],
    "talk-about-your-plan": [
      "doctor",
      "speech",
      "question",
      "question",
      "support"
    ],
    "tests-and-treatment": [
      "speech",
      "consent",
      "pause",
      "question"
    ],
    "visitors-and-support": [
      "visitors",
      "support",
      "phone"
    ],
    "leaving-hospital": [
      "medicine",
      "calendar",
      "speech",
      "phone"
    ]
  },
  "kch-ld-day-surgery": {
    "before-you-come": [
      "health",
      "speech",
      "help"
    ],
    "arriving": [
      "letter",
      "medicine",
      "directions",
      "support"
    ],
    "checks": [
      "nurse",
      "health",
      "comfort",
      "question"
    ],
    "procedure": [
      "doctor",
      "speech",
      "consent"
    ],
    "recovery": [
      "pain",
      "medicine",
      "food"
    ],
    "going-home": [
      "support",
      "visitors",
      "phone"
    ]
  }
});
export const EASY_READ_INTRO_VISUAL_KEYS = Object.freeze({
  "generic-easy-read-outpatient-appointment": {
    "before-you-go": [
      "letter"
    ],
    "arrive-and-check-in": [
      "reception"
    ],
    "waiting": [
      "clock"
    ],
    "your-appointment": [
      "doctor"
    ],
    "after-your-appointment": [
      "next"
    ]
  },
  "generic-easy-read-scan": {
    "before-your-scan": [
      "letter"
    ],
    "arrive": [
      "reception"
    ],
    "get-ready": [
      "speech"
    ],
    "have-the-scan": [
      "still",
      "scan"
    ],
    "after-the-scan": [
      "home"
    ]
  },
  "generic-easy-read-blood-test": {
    "before-your-test": [
      "letter"
    ],
    "arrive": [
      "reception"
    ],
    "get-ready": [
      "speech"
    ],
    "take-the-blood": [
      "needle"
    ],
    "after-the-test": [
      "needle",
      "health"
    ]
  },
  "generic-easy-read-emergency-department": {
    "arrive": [
      "reception"
    ],
    "first-check": [
      "health",
      "help"
    ],
    "wait": [
      "clock"
    ],
    "tests-and-treatment": [
      "doctor",
      "treatment"
    ],
    "what-happens-next": [
      "home",
      "health",
      "hospital"
    ]
  },
  "generic-easy-read-hospital-stay": {
    "arrive-on-the-ward": [
      "directions"
    ],
    "daily-care": [
      "health"
    ],
    "talk-about-your-plan": [
      "health",
      "next"
    ],
    "tests-and-treatment": [
      "treatment"
    ],
    "visitors-and-support": [
      "visitors"
    ],
    "leaving-hospital": [
      "home"
    ]
  },
  "kch-ld-day-surgery": {
    "before-you-come": [
      "letter",
      "health"
    ],
    "arriving": [
      "reception"
    ],
    "checks": [
      "health"
    ],
    "procedure": [
      "treatment"
    ],
    "recovery": [
      "chair"
    ],
    "going-home": [
      "home"
    ]
  }
});

export const EASY_READ_VISUAL_LABELS = Object.freeze({
  "letter": "Hospital letter or instructions",
  "support": "Help from someone you trust",
  "medicine": "Medicines",
  "speech": "Talking with hospital staff",
  "help": "Asking for help",
  "id": "Name and personal details",
  "reception": "Hospital reception",
  "noise": "Noise and busy places",
  "question": "Asking a question",
  "health": "Health check",
  "clock": "Waiting or extra time",
  "quiet": "A quiet place",
  "next": "What happens next",
  "doctor": "Talking with a doctor",
  "treatment": "Tests or treatment",
  "calendar": "Future appointments",
  "notes": "Written notes",
  "phone": "Contact the care team",
  "scan": "Hospital scan",
  "bag": "Things to bring",
  "clothes": "Changing clothes",
  "metal": "Metal items",
  "arm": "Your arm",
  "comfort": "Feeling worried or uncomfortable",
  "still": "Keeping still",
  "headphones": "Hearing protection",
  "stop": "Asking to stop",
  "home": "Going home",
  "result": "Test result",
  "blood": "Blood test",
  "food": "Food and drink",
  "needle": "Needle or blood test",
  "chair": "Sitting in a chair",
  "pause": "Taking a break",
  "ambulance": "An ambulance",
  "pain": "Pain or feeling unwell",
  "allergy": "Medicines and allergies",
  "warning": "Important warning signs",
  "safety": "Keeping you safe",
  "nurse": "Nurse or healthcare worker",
  "passport": "Hospital passport",
  "consent": "Understanding and agreeing",
  "visitors": "Family or visitors",
  "directions": "Finding where to go",
  "hospital": "Staying in hospital"
});

export function splitEasyReadSentences(body = []) {
  return body.flatMap((line) => String(line || '').trim().split(/(?<=[.!?])\s+(?=[A-Z])/).filter(Boolean));
}

export function getEasyReadStageRows(journey, stage) {
  // Include the stage introduction too: it can contain instructions or
  // information which must not disappear from the printed version.
  const introLines = splitEasyReadSentences(stage?.description ? [stage.description] : []);
  const bodyLines = splitEasyReadSentences(stage?.body || []);
  const lines = [...introLines, ...bodyLines];
  const introKeys = EASY_READ_INTRO_VISUAL_KEYS[journey?.id]?.[stage?.id] || [];
  const bodyKeys = EASY_READ_VISUAL_KEYS[journey?.id]?.[stage?.id] || [];
  const keys = [...introKeys, ...bodyKeys];
  return lines.map((text, index) => {
    const picture = stage?.easyReadRowPictures?.[index] || null;
    // A real photograph needs an explicit accessible description and approval.
    // Never automatically choose a fictional image of hospital facilities.
    const approvedPicture = picture?.src && picture?.alt && picture?.approved === true ? picture : null;
    const cue = keys[index] || null;
    return {
      id: `${stage?.id || 'stage'}-${index}`,
      text,
      isIntroduction: index < introLines.length,
      cue,
      cueLabel: EASY_READ_VISUAL_LABELS[cue] || 'Picture needed for review',
      picture: approvedPicture,
      needsPictureReview: !approvedPicture,
    };
  });
}
